import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { showPopover } from "@core-ui/showPopover";
import { useSettings } from "@ext/enterprise/components/admin/contexts/SettingsContext";
import { useOpenState } from "@ext/enterprise/components/admin/hooks/useOpenState";
import { ChangeRoleButton } from "@ext/enterprise/components/admin/settings/members/components/ChangeRoleButton";
import type { UserAccessPickerDialogContentProps } from "@ext/enterpriseCommon/components/userAccessPicker/types/userAccessPickerDialogContentTypes";
import { UserAccessPickerDialogTable } from "@ext/enterpriseCommon/components/userAccessPicker/UserAccessPickerDialogTable";
import { useUserAccessPickerDialogContent } from "@ext/enterpriseCommon/hooks/useUserAccessPickerDialogContent";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { useCallback, useMemo, useState } from "react";
import { GesCloudApi } from "../../GesCloudApi";
import { InviteMemberCard } from "../organizationSettings/settings/components/InviteMemberCard";
import { isOrganizationOwner } from "../organizationSettings/settings/members/isOrganizationOwner";
import type {
	GesCloudMember,
	InviteMemberPayload,
} from "../organizationSettings/settings/members/types/GesCloudUsersComponentTypes";

export const GesCloudUserAccessPickerDialogContent = (props: UserAccessPickerDialogContentProps) => {
	const { ensureLoaded } = useSettings();
	const gesCloudUrl = PageDataContextService.value?.conf?.enterpriseCloud?.url;
	const gesCloudApi = useMemo(() => (gesCloudUrl ? new GesCloudApi(gesCloudUrl) : null), [gesCloudUrl]);
	const extraUsers = useMemo(() => [], []);
	const { data, headerLeftControls, actions } = useUserAccessPickerDialogContent({ ...props, extraUsers });

	const inviteSheet = useOpenState({ keyBase: "invite" });
	const [members, setMembers] = useState<GesCloudMember[]>([]);
	const currentUserMail = PageDataContextService.value?.user?.info?.mail;
	const isCurrentUserOwner = useMemo(() => isOrganizationOwner(members, currentUserMail), [members, currentUserMail]);
	const existingUsers = useMemo(() => members.map((member) => member.email), [members]);

	const openInviteSheet = useCallback(async () => {
		if (!gesCloudApi) return;
		try {
			setMembers(await gesCloudApi.getMembers());
			inviteSheet.open();
		} catch {
			showPopover(t("enterprise-cloud.org-settings.users.invite-form.errors.generic"));
		}
	}, [gesCloudApi, inviteSheet.open]);

	const handleInviteMember = useCallback(
		async (invite: InviteMemberPayload) => {
			if (!gesCloudApi) return;

			await gesCloudApi.inviteMember({
				email: invite.email,
				workspaceRole: invite.workspaceRole,
				membershipRole: invite.membershipRole,
				aiAgentAccess: invite.aiAgentAccess,
				resources: [],
			});

			actions.createNew(invite.email);

			await ensureLoaded("workspace", true);

			showPopover(t("enterprise-cloud.org-settings.users.invite-toast"));
		},
		[actions.createNew, ensureLoaded, gesCloudApi],
	);

	const toolbarActions = (
		<>
			<ChangeRoleButton
				count={props.selectedIds.length}
				onChange={actions.bulkRoleChange}
				rules={data.roleRules}
			/>
			<Button className="pl-2.5 pr-3" onClick={() => void openInviteSheet()} startIcon="plus" variant="outline">
				{t("enterprise.admin.users.add")}
			</Button>
			<InviteMemberCard
				aggregate={props.aggregate}
				canManageAdmins={isCurrentUserOwner}
				existingUsers={existingUsers}
				key={inviteSheet.key}
				onClose={inviteSheet.close}
				onInvite={handleInviteMember}
				open={inviteSheet.isOpen}
				showAccesses={false}
			/>
		</>
	);

	return (
		<UserAccessPickerDialogTable
			data={data}
			headerLeftControls={headerLeftControls}
			selection={props.selection}
			setSelection={props.setSelection}
			toolbarActions={toolbarActions}
		/>
	);
};
